import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import axios from 'utils/axios';
import AxiosAdmin from 'utils/axiosAdmin';
import URL from 'config/url';

const formatQueryParam = (value) => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.length > 0 ? value.map(String).join(',') : '';
  return String(value);
};

export const buildCarBookingsQuery = (data, options = {}) => {
  const { csv = false } = options;
  const params = new URLSearchParams();

  params.set('page', String(data.page || 1));
  params.set('limit', String(data.limit || 10));
  if (data.pageSize !== undefined && data.pageSize !== null) {
    params.set('page_size', String(data.pageSize));
  }
  if (csv) params.set('csv', 'true');
  if (data.id) params.set('id', data.id);

  params.set('booking_start_date', data.booking_start_date ?? data.startDate ?? '');
  params.set('booking_end_date', data.booking_end_date ?? data.endDate ?? '');
  params.set('travel_start_date', data.travel_start_date ?? data.travelStartDate ?? '');
  params.set('travel_end_date', data.travel_end_date ?? data.travelEndDate ?? '');
  params.set('booking_status', formatQueryParam(data.booking_status ?? data.bookingStatus));
  params.set('provider_name', formatQueryParam(data.provider_name ?? data.provider));
  params.set('currency', formatQueryParam(data.currency));
  params.set('account_name', formatQueryParam(data.account_name ?? data.agencyName));
  params.set('account_id', formatQueryParam(data.account_id ?? data.agencyId));
  params.set('text', data.text ?? '');

  const sortingField = data.sorting_field ?? data.sortingField;
  if (sortingField) params.set('sorting_field', sortingField);

  const sorting = data.sorting;
  if (sorting === 'ascend') params.set('sorting', 'asc');
  else if (sorting === 'descend') params.set('sorting', 'desc');
  else if (sorting === 'asc' || sorting === 'desc') params.set('sorting', sorting);

  params.set('payment_gateway', formatQueryParam(data.payment_gateway ?? data.paymentGateway));
  params.set('payment_type', formatQueryParam(data.payment_method_type ?? data.paymentMethodTypes));
  params.set('user_type', formatQueryParam(data.user_type ?? data.userType));
  params.set('cogs', formatQueryParam(data.cogs));
  params.set('non_login', formatQueryParam(data.non_login ?? data.isNonLogin));
  params.set('cogs_paid_date', data.cogs_paid_date ?? data.cogsPaidDate ?? '');
  params.set('agency_commission_paid', formatQueryParam(data.agency_commission_paid ?? data.agencyCommissionPaid));
  params.set('agency_commission_paid_date', data.agency_commission_paid_date ?? data.agencyCommissionPaidDate ?? '');

  return params.toString();
};

export const getCars = createAsyncThunk('GET CARS', async (data, thunkAPI) => {
  const query = buildCarBookingsQuery(data);
  const response = await AxiosAdmin.get(`${URL.CAR_V2}?${query}`, thunkAPI);
  return response.data;
});

export const getCarDetails = createAsyncThunk('GET CAR DETAILS', async (data, thunkAPI) => {
  const response = await AxiosAdmin.get(
    `${URL.CAR_V2}?id=${data.referenceNumber}&page_size=1&limit=1&page=1`,
    thunkAPI
  );
  return response.data;
});

export const resendEmailConfirmation = createAsyncThunk(
  'RESEND_CAR_EMAIL_CONFIRMATION',
  async ({ requestData, v2Version }, thunkAPI) => {
    if (v2Version) {
      const response = await AxiosAdmin.post(URL.CAR_NOTIFY_EMAIL_V2, requestData, thunkAPI);
      return response.data;
    } else {
      const response = await axios.post(URL.RESEND_CAR_CONFIRMATION_EMAIL, requestData, thunkAPI);
      return response.data;
    }
  }
);

export const updateCarRemark = createAsyncThunk('UPDATE_CAR_REMARKS', async (data, thunkAPI) => {
  let requestData = {
    remark_id: data?.remark_id || "",
    remark: data?.remark,
    username: data?.username || "",
  };
  const response = await AxiosAdmin.put(`${URL.CAR_UPDATE_V2}?id=${data.referenceNumber}`, requestData, thunkAPI);
  return response.data;
});

export const updateCarBooking = createAsyncThunk('UPDATE_CAR_BOOKING', async (data, thunkAPI) => {
  const response = await AxiosAdmin.put(`${URL.CAR_UPDATE_V2}?id=${data.referenceNumber}`, data, thunkAPI);
  return response.data;
});

const initialState = {
  isLoading: false,
  carData: null,
  carDetails: null,
};

export const carSlice = createSlice({
  name: 'car',
  initialState,
  reducers: {},
  extraReducers: {
    // get cars
    [getCars.pending]: (state) => {
      state.isLoading = true;
      state.carData = null;
    },
    [getCars.fulfilled]: (state, action) => {
      state.isLoading = false;
      state.carData = action.payload.data;
    },
    [getCars.rejected]: (state, action) => {
      state.isLoading = false;
      state.carData = null;
      toast.error(action?.payload?.message);
    },

    // update car remarks
    [updateCarRemark.pending]: (state) => {
      state.isLoading = true;
    },
    [updateCarRemark.fulfilled]: (state) => {
      state.isLoading = false;
      toast.success('Car remarks updated successfully');
    },
    [updateCarRemark.rejected]: (state, action) => {
      state.isLoading = false;
      toast.error(action.payload?.message || 'Error updating car remarks');
    },
    // get activity Details
    [getCarDetails.pending]: (state) => {
      state.isLoading = true;
      state.carDetails = null;
    },
    [getCarDetails.fulfilled]: (state, action) => {
      state.isLoading = false;
      const data = action.payload.data;
      state.carDetails = data?.bookings?.[0] || data;
    },
    [getCarDetails.rejected]: (state, action) => {
      state.isLoading = false;
      state.carDetails = null;
      toast.error(action?.payload?.message);
    },

    // resend hotel confirmation email
    [resendEmailConfirmation.pending]: (state) => {
      state.isLoading = true;
    },
    [resendEmailConfirmation.fulfilled]: (state) => {
      state.isLoading = false;
      // Handle success, e.g., show a success toast message
      toast.success('Email confirmation resent successfully');
    },
    [resendEmailConfirmation.rejected]: (state, action) => {
      state.isLoading = false;
      // Handle error, e.g., show an error toast message
      toast.error(action.payload?.message || 'Error resending email confirmation');
    },

    // update car booking
    [updateCarBooking.pending]: (state) => {
      state.isLoading = true;
    },
    [updateCarBooking.fulfilled]: (state, action) => {
      state.isLoading = false;
      toast.success(action.payload?.message);
    },
    [updateCarBooking.rejected]: (state, action) => {
      state.isLoading = false;
      toast.error(action.payload?.message || 'Error updating cogs data');
    },
  },
});

export default carSlice.reducer;
